import AdminProductUpdates from "../components/AdminProductUpdates";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { config } from '../Constants';
import AdminStockEditor from '../components/AdminStockEditor';
import StockAutocompleteInput from '../components/StockAutocompleteInput/StockAutocompleteInput';

const URL = config.url;

const emptyStockForm = {
    sku: '',
    name: '',
    manufacturer: '',
    productCategory: 'GPU',
    regularPrice: '',
    salePrice: '',
    releaseDate: '',
    inStoreAvailability: false,
    onlineAvailability: false,
    inStoreAvailabilityUpdateDate: '',
    onlineAvailabilityUpdateDate: '',
    url: '',
    addToCartUrl: '',
    image: '',
    thumbnailImage: '',
    alternateViewsImage: '',
    condition: '',
    longDescription: ''
};

const emptyMcOfferForm = {
    id: null,
    mcProductId: '',
    mcSku: '',
    regularPrice: '',
    salePrice: '',
    onSale: false,
    url: '',
    inStoreAvailability: false,
    onlineAvailability: false,
    inStoreAvailabilityUpdateDate: '',
    onlineAvailabilityUpdateDate: '',
    inStoreAvailabilityUpdatedAt: '',
    onlineAvailabilityUpdatedAt: '',
    createdAt: '',
    updatedAt: ''
};

const roleOptions = ['USER', 'ADMIN'];

const AdminPage = () => {
    const { isAdmin, authToken } = useAuth();
    const [tabIndex, setTabIndex] = useState(0);
    const [stocks, setStocks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stockSearch, setStockSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [selectedSku, setSelectedSku] = useState(null);
    const [selectedStockId, setSelectedStockId] = useState(null);
    const [retailerOffers, setRetailerOffers] = useState([]);
    const [editorMode, setEditorMode] = useState('create');
    const [editorForm, setEditorForm] = useState(emptyStockForm);
    const [mcOfferForm, setMcOfferForm] = useState(emptyMcOfferForm);
    const [hasMcOffer, setHasMcOffer] = useState(false);
    const [mcOfferExpanded, setMcOfferExpanded] = useState(false);
    const [mcStateData, setMcStateData] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [importSkuInput, setImportSkuInput] = useState('');
    const [stockPage, setStockPage] = useState(0);
    const [stockRowsPerPage, setStockRowsPerPage] = useState(10);
    const [stockTotal, setStockTotal] = useState(0);
    const [userPage, setUserPage] = useState(0);
    const [userRowsPerPage, setUserRowsPerPage] = useState(10);
    const [userTotal, setUserTotal] = useState(0);
    // The user a complimentary membership is being granted to, and the day it should run through.
    const [comp, setComp] = useState(null);

    const authHeaders = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : ''
    }), [authToken]);

    const normalizeDate = (value) => {
        if (!value) {
            return '';
        }

        let date;
        if (typeof value === 'number') {
            const millis = value < 1_000_000_000_000 ? value * 1000 : value;
            date = new Date(millis);
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (typeof value.epochSecond === 'number') {
                const millis = value.epochSecond * 1000 + Math.floor((value.nano || 0) / 1_000_000);
                date = new Date(millis);
            } else {
                date = new Date(value);
            }
        } else if (Array.isArray(value) && value.length >= 3) {
            const [year, month, day] = value;
            date = new Date(Date.UTC(year, month - 1, day));
        } else {
            date = new Date(value);
        }

        if (Number.isNaN(date.getTime())) {
            return '';
        }
        return date.toISOString().slice(0, 10);
    };

    const startCreate = () => {
        setEditorMode('create');
        setSelectedSku(null);
        setSelectedStockId(null);
        setRetailerOffers([]);
        setEditorForm(emptyStockForm);
        setMcOfferForm(emptyMcOfferForm);
        setHasMcOffer(false);
        setMcOfferExpanded(false);
        setMcStateData([]);
        setStateData([]);
        setError('');
    };

    const mapMcOfferToForm = (offer) => ({
        id: offer?.id ?? null,
        mcProductId: offer?.mcProductId ?? '',
        mcSku: offer?.mcSku ?? '',
        regularPrice: offer?.regularPrice ?? '',
        salePrice: offer?.salePrice ?? '',
        onSale: !!offer?.onSale,
        url: offer?.url ?? '',
        inStoreAvailability: !!offer?.inStoreAvailability,
        onlineAvailability: !!offer?.onlineAvailability,
        inStoreAvailabilityUpdateDate: normalizeDate(offer?.inStoreAvailabilityUpdatedAt),
        onlineAvailabilityUpdateDate: normalizeDate(offer?.onlineAvailabilityUpdatedAt),
        inStoreAvailabilityUpdatedAt: offer?.inStoreAvailabilityUpdatedAt ?? '',
        onlineAvailabilityUpdatedAt: offer?.onlineAvailabilityUpdatedAt ?? '',
        createdAt: offer?.createdAt ?? '',
        updatedAt: offer?.updatedAt ?? ''
    });

    const fetchMcOffer = async (sku) => {
        if (!sku) {
            setMcOfferForm(emptyMcOfferForm);
            setHasMcOffer(false);
            setMcOfferExpanded(false);
            setMcStateData([]);
            return null;
        }

        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${sku}/mc-offer`, {
                headers: authHeaders,
                credentials: 'include'
            });

            if (response.status === 404) {
                setMcOfferForm(emptyMcOfferForm);
                setHasMcOffer(false);
                setMcOfferExpanded(false);
                setMcStateData([]);
                return null;
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load MC offer');
            }

            if (!data?.data) {
                setMcOfferForm(emptyMcOfferForm);
                setHasMcOffer(false);
                setMcOfferExpanded(false);
                setMcStateData([]);
                return null;
            }

            setMcOfferForm(mapMcOfferToForm(data.data));
            setHasMcOffer(true);
            setMcOfferExpanded(true);
            return data.data;
        } catch (err) {
            setMcOfferForm(emptyMcOfferForm);
            setHasMcOffer(false);
            setMcOfferExpanded(false);
            setMcStateData([]);
            setError(err.message);
            return null;
        }
    };

    const fetchMcStateAvailability = async (sku) => {
        if (!sku) {
            setMcStateData([]);
            return;
        }

        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${sku}/mc-offer/states`, {
                headers: authHeaders,
                credentials: 'include'
            });

            if (response.status === 404) {
                setMcStateData([]);
                return;
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load MC state availability');
            }

            setMcStateData(Array.isArray(data.data) ? data.data : []);
        } catch (err) {
            setMcStateData([]);
            setError(err.message);
        }
    };

    const startEdit = (stock) => {
        setEditorMode('edit');
        setSelectedSku(stock.sku);
        setSelectedStockId(stock.id ?? null);
        setEditorForm({
            sku: stock.sku ?? '',
            name: stock.name ?? '',
            manufacturer: stock.manufacturer ?? '',
            productCategory: stock.productCategory ?? 'GPU',
            regularPrice: stock.regularPrice ?? '',
            salePrice: stock.salePrice ?? '',
            releaseDate: normalizeDate(stock.releaseDate),
            inStoreAvailability: !!stock.inStoreAvailability,
            onlineAvailability: !!stock.onlineAvailability,
            inStoreAvailabilityUpdateDate: normalizeDate(stock.inStoreAvailabilityUpdateDate),
            onlineAvailabilityUpdateDate: normalizeDate(stock.onlineAvailabilityUpdateDate),
            url: stock.url ?? '',
            addToCartUrl: stock.addToCartUrl ?? '',
            image: stock.image ?? '',
            thumbnailImage: stock.thumbnailImage ?? '',
            alternateViewsImage: stock.alternateViewsImage ?? '',
            condition: stock.condition ?? '',
            longDescription: stock.longDescription ?? ''
        });
        setMcOfferForm(emptyMcOfferForm);
        setHasMcOffer(false);
        setMcOfferExpanded(false);
        setMcStateData([]);
        setRetailerOffers([]);
        fetchStateAvailability(stock.sku);
        void (async () => {
            const mcOffer = await fetchMcOffer(stock.sku);
            if (mcOffer) {
                await fetchMcStateAvailability(stock.sku);
            }
        })();
        void fetchRetailerOffers(stock.id);
        setError('');
    };

    const fetchRetailerOffers = async (stockId) => {
        if (!stockId) {
            setRetailerOffers([]);
            return;
        }

        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${stockId}/retailer-offers`, {
                headers: authHeaders,
                credentials: 'include'
            });

            if (response.status === 404) {
                setRetailerOffers([]);
                return;
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load retailer offers');
            }

            setRetailerOffers(Array.isArray(data.data) ? data.data : []);
        } catch (err) {
            setRetailerOffers([]);
            setError(err.message);
        }
    };

    const handleAddRetailerOffer = async (payload) => {
        if (!selectedStockId) {
            setError('Select a stock before attaching a retailer offer.');
            return false;
        }

        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${selectedStockId}/retailer-offers`, {
                method: 'POST',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to attach retailer offer');
            }

            await fetchRetailerOffers(selectedStockId);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRetailerOffer = async (offer) => {
        if (!offer?.id) {
            return;
        }
        const confirmed = window.confirm(`Remove ${offer.retailer} offer ${offer.externalItemNumber}?`);
        if (!confirmed) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/retailer-offers/${offer.id}`, {
                method: 'DELETE',
                headers: authHeaders,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to remove retailer offer');
            }

            await fetchRetailerOffers(selectedStockId);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStocks = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                size: String(stockRowsPerPage),
                page: String(stockPage)
            });
            if (appliedSearch) {
                params.set('searchTerm', appliedSearch);
            }
            const response = await fetch(`${URL}/api/v1/admin/stocks?${params.toString()}`, {
                headers: authHeaders,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load stocks');
            }
            setStocks(data.data?.content || []);
            setStockTotal(data.data?.totalElements || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [appliedSearch, authHeaders, stockPage, stockRowsPerPage]);

    const fetchStateAvailability = async (sku) => {
        if (!sku) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${sku}/states`, {
                headers: authHeaders,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load state availability');
            }
            setStateData(Array.isArray(data.data) ? data.data : []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                size: String(userRowsPerPage),
                page: String(userPage)
            });
            const response = await fetch(`${URL}/api/v1/admin/users?${params.toString()}`, {
                headers: authHeaders,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load users');
            }
            setUsers(data.data?.content || []);
            setUserTotal(data.data?.totalElements || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authHeaders, userPage, userRowsPerPage]);

    useEffect(() => {
        if (!isAdmin) {
            return;
        }
        if (tabIndex === 0) {
            fetchStocks();
        } else if (tabIndex === 1) {
            fetchUsers();
        }
    }, [fetchStocks, fetchUsers, isAdmin, tabIndex]);

    const parseOptionalNumber = (value) => {
        if (value === '' || value === null || value === undefined) {
            return null;
        }
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const inferOnSale = (regularPriceValue, salePriceValue) => {
        const regular = parseOptionalNumber(regularPriceValue);
        const sale = parseOptionalNumber(salePriceValue);
        return regular !== null && sale !== null && sale > 0 && sale < regular;
    };

    const buildPayload = () => ({
        sku: editorForm.sku ? Number(editorForm.sku) : null,
        name: editorForm.name || null,
        manufacturer: editorForm.manufacturer || null,
        productCategory: editorForm.productCategory || 'GPU',
        regularPrice: parseOptionalNumber(editorForm.regularPrice),
        salePrice: parseOptionalNumber(editorForm.salePrice),
        onSale: inferOnSale(editorForm.regularPrice, editorForm.salePrice),
        releaseDate: editorForm.releaseDate || null,
        inStoreAvailability: editorForm.inStoreAvailability,
        onlineAvailability: editorForm.onlineAvailability,
        inStoreAvailabilityUpdateDate: editorForm.inStoreAvailabilityUpdateDate || null,
        onlineAvailabilityUpdateDate: editorForm.onlineAvailabilityUpdateDate || null,
        url: editorForm.url || null,
        addToCartUrl: editorForm.addToCartUrl || null,
        image: editorForm.image || null,
        thumbnailImage: editorForm.thumbnailImage || null,
        alternateViewsImage: editorForm.alternateViewsImage || null,
        condition: editorForm.condition || null,
        longDescription: editorForm.longDescription || null
    });

    const buildMcOfferPayload = () => ({
        mcProductId: mcOfferForm.mcProductId?.trim(),
        mcSku: mcOfferForm.mcSku?.trim() || null,
        regularPrice: parseOptionalNumber(mcOfferForm.regularPrice),
        salePrice: parseOptionalNumber(mcOfferForm.salePrice),
        onSale: inferOnSale(mcOfferForm.regularPrice, mcOfferForm.salePrice),
        url: mcOfferForm.url?.trim() || null,
        inStoreAvailability: !!mcOfferForm.inStoreAvailability,
        onlineAvailability: !!mcOfferForm.onlineAvailability,
        inStoreAvailabilityUpdateDate: mcOfferForm.inStoreAvailabilityUpdateDate || null,
        onlineAvailabilityUpdateDate: mcOfferForm.onlineAvailabilityUpdateDate || null
    });

    const upsertMcOffer = async (stockSku) => {
        if (!stockSku) {
            throw new Error('Select a stock before saving a Micro Center offer.');
        }
        if (!mcOfferForm.mcProductId || !mcOfferForm.mcProductId.trim()) {
            throw new Error('Use a valid Micro Center product URL and click Import URL before saving.');
        }

        const payload = buildMcOfferPayload();
        const methods = hasMcOffer ? ['PUT', 'POST'] : ['POST', 'PUT'];

        let data = null;
        let success = false;
        let lastErrorMessage = 'Failed to save Micro Center offer';

        for (const method of methods) {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${stockSku}/mc-offer`, {
                method,
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            const responseData = await response.json();

            if (response.ok) {
                data = responseData;
                success = true;
                break;
            }

            lastErrorMessage = responseData?.message || lastErrorMessage;
            if (![404, 409].includes(response.status)) {
                break;
            }
        }

        if (!success) {
            throw new Error(lastErrorMessage);
        }

        if (data?.data) {
            setMcOfferForm(mapMcOfferToForm(data.data));
            setHasMcOffer(true);
            setMcOfferExpanded(true);
        }

        return data?.data || null;
    };

    const handleSaveStock = async () => {
        if (!editorForm.sku) {
            setError('SKU is required.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const payload = buildPayload();
            const endpoint = editorMode === 'create'
                ? `${URL}/api/v1/admin/stocks`
                : `${URL}/api/v1/admin/stocks/${editorForm.sku}`;
            const method = editorMode === 'create' ? 'POST' : 'PUT';
            const response = await fetch(endpoint, {
                method,
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to save stock');
            }

            const savedStock = data?.data || null;
            const persistedSku = editorMode === 'create'
                ? savedStock?.sku
                : editorForm.sku;

            const shouldUpsertMcOffer = !!persistedSku && (hasMcOffer || mcOfferExpanded);
            const hasMcProductIdInput = !!mcOfferForm.mcProductId?.trim();
            if (shouldUpsertMcOffer && (hasMcOffer || hasMcProductIdInput)) {
                await upsertMcOffer(persistedSku);
                await fetchMcStateAvailability(persistedSku);
            }

            if (editorMode === 'create') {
                if (savedStock?.sku) {
                    startEdit(savedStock);
                } else {
                    startCreate();
                }
            } else if (persistedSku && (hasMcOffer || shouldUpsertMcOffer)) {
                await fetchMcOffer(persistedSku);
            }

            await fetchStocks();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteStock = async () => {
        if (!selectedSku) {
            setError('Select a stock to delete.');
            return;
        }
        const confirmDelete = window.confirm(`Delete SKU ${selectedSku}? This cannot be undone.`);
        if (!confirmDelete) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${selectedSku}`, {
                method: 'DELETE',
                headers: authHeaders,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to delete stock');
            }
            startCreate();
            await fetchStocks();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMcOffer = async () => {
        if (!selectedSku) {
            return;
        }
        const confirmed = window.confirm(`Remove Micro Center offer for SKU ${selectedSku}?`);
        if (!confirmed) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${selectedSku}/mc-offer`, {
                method: 'DELETE',
                headers: authHeaders,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to remove MC offer');
            }

            setMcOfferForm(emptyMcOfferForm);
            setHasMcOffer(false);
            setMcOfferExpanded(false);
            setMcStateData([]);
            await fetchStocks();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportMcOfferFromUrl = async () => {
        if (!selectedSku) {
            setError('Select a stock before importing a Micro Center URL.');
            return;
        }

        const url = mcOfferForm.url?.trim();
        if (!url) {
            setError('Paste a Micro Center product URL first.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${selectedSku}/mc-offer/import-from-url`, {
                method: 'POST',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify({ url })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to import Micro Center offer from URL');
            }

            setMcOfferForm(mapMcOfferToForm(data.data));
            setHasMcOffer(true);
            setMcOfferExpanded(true);
            await fetchMcStateAvailability(selectedSku);
            await fetchStocks();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportSkus = async () => {
        const skus = importSkuInput
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
            .map((value) => Number(value))
            .filter((value) => !Number.isNaN(value));

        if (skus.length === 0) {
            setError('Enter at least one valid SKU to import.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/import`, {
                method: 'POST',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify({ skus })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to import SKUs');
            }
            setImportSkuInput('');
            await fetchStocks();
            const first = Array.isArray(data.data) ? data.data[0] : null;
            if (first?.sku) {
                startEdit(first);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleState = async (stateCode) => {
        if (!selectedSku) {
            return;
        }
        const previous = stateData;
        const next = stateData.map((state) => (
            state.stateCode === stateCode
                ? { ...state, available: !state.available }
                : state
        ));
        setStateData(next);

        const target = next.find((state) => state.stateCode === stateCode);
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${selectedSku}/states/${stateCode}`, {
                method: 'PUT',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify({ available: !!target?.available })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to update state availability');
            }
            const hasInStoreAvailability = next.some((state) => state.available);
            setEditorForm((previousForm) => ({
                ...previousForm,
                inStoreAvailability: hasInStoreAvailability,
                inStoreAvailabilityUpdateDate: new Date().toISOString().slice(0, 10)
            }));
        } catch (err) {
            setStateData(previous);
            setError(err.message);
        }
    };

    const handleToggleMcState = async (stateCode) => {
        if (!selectedSku) {
            return;
        }

        const previous = mcStateData;
        const next = mcStateData.map((state) => (
            state.stateCode === stateCode
                ? { ...state, available: !state.available }
                : state
        ));
        setMcStateData(next);

        const target = next.find((state) => state.stateCode === stateCode);
        try {
            const response = await fetch(`${URL}/api/v1/admin/stocks/${selectedSku}/mc-offer/states/${stateCode}`, {
                method: 'PUT',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify({ available: !!target?.available })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to update MC state availability');
            }
            const hasInStoreAvailability = next.some((state) => state.available);
            setMcOfferForm((previousForm) => ({
                ...previousForm,
                inStoreAvailability: hasInStoreAvailability,
                inStoreAvailabilityUpdatedAt: new Date().toISOString(),
                inStoreAvailabilityUpdateDate: new Date().toISOString().slice(0, 10)
            }));
        } catch (err) {
            setMcStateData(previous);
            setError(err.message);
        }
    };

    const handleRoleChange = async (userId, role) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/users/${userId}/role`, {
                method: 'PUT',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify({ role })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to update user role');
            }
            await fetchUsers();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const setComplimentary = async (userId, until) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${URL}/api/v1/admin/users/${userId}/complimentary`, {
                method: 'PUT',
                headers: authHeaders,
                credentials: 'include',
                body: JSON.stringify({ until })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to update complimentary membership');
            }
            await fetchUsers();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGrantComp = async () => {
        const { user, date } = comp;
        setComp(null);
        // The end of the chosen day: granting "until the 30th" means through the 30th, and
        // midnight would take that last day back.
        await setComplimentary(user.id, `${date}T23:59:59`);
    };

    if (!isAdmin) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Admin access required
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        You do not have permission to view this page.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 6 }}>
            <Paper sx={{ p: { xs: 2, md: 4 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            Admin Dashboard
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage stocks and users.
                        </Typography>
                    </Box>
                    <Tabs
                        value={tabIndex}
                        onChange={(event, newValue) => setTabIndex(newValue)}
                        sx={{ alignSelf: 'center' }}
                    >
                        <Tab label="Stocks" />
                        <Tab label="Users" />
                        <Tab label="Product updates" />
                    </Tabs>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {tabIndex === 0 && (
                    <Stack spacing={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                <StockAutocompleteInput
                                    value={stockSearch}
                                    onValueChange={setStockSearch}
                                    onSubmit={(term) => {
                                        setAppliedSearch(term.trim());
                                        setStockPage(0);
                                    }}
                                    label="Search stocks"
                                    disabled={loading}
                                />
                                <TextField
                                    label="Import SKUs (comma or space separated)"
                                    value={importSkuInput}
                                    onChange={(event) => setImportSkuInput(event.target.value)}
                                />
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setAppliedSearch(stockSearch.trim());
                                            setStockPage(0);
                                        }}
                                        disabled={loading}
                                    >
                                        Search
                                    </Button>
                                    <Button variant="outlined" onClick={handleImportSkus} disabled={loading}>
                                        Import
                                    </Button>
                                    <Button variant="contained" onClick={startCreate} disabled={loading}>
                                        New Stock
                                    </Button>
                                </Stack>
                            </Stack>
                            <TableContainer sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell>Manufacturer</TableCell>
                                            <TableCell>Price</TableCell>
                                            <TableCell>In-store</TableCell>
                                            <TableCell>Online</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stocks.map((stock) => {
                                            const combinedInStoreAvailability = stock.combinedInStoreAvailability ?? stock.inStoreAvailability;
                                            const combinedOnlineAvailability = stock.combinedOnlineAvailability ?? stock.onlineAvailability;

                                            return (
                                            <TableRow
                                                key={stock.sku}
                                                hover
                                                selected={selectedSku === stock.sku}
                                                onClick={() => startEdit(stock)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell>{stock.sku}</TableCell>
                                                <TableCell>{stock.name || 'Unnamed'}</TableCell>
                                                <TableCell>{stock.productCategory || 'GPU'}</TableCell>
                                                <TableCell>{stock.manufacturer || '—'}</TableCell>
                                                <TableCell>{stock.regularPrice ?? '—'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={combinedInStoreAvailability ? 'Yes' : 'No'}
                                                        color={combinedInStoreAvailability ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={combinedOnlineAvailability ? 'Yes' : 'No'}
                                                        color={combinedOnlineAvailability ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={stockTotal}
                                page={stockPage}
                                onPageChange={(event, newPage) => setStockPage(newPage)}
                                rowsPerPage={stockRowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setStockRowsPerPage(parseInt(event.target.value, 10));
                                    setStockPage(0);
                                }}
                                rowsPerPageOptions={[10, 20, 50]}
                            />
                        </Paper>

                        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                            <AdminStockEditor
                                form={editorForm}
                                mode={editorMode}
                                loading={loading}
                                selectedSku={selectedSku}
                                onChange={setEditorForm}
                                onSave={handleSaveStock}
                                onDelete={handleDeleteStock}
                                stateData={stateData}
                                onToggleState={handleToggleState}
                                mcOfferForm={mcOfferForm}
                                hasMcOffer={hasMcOffer}
                                mcOfferExpanded={mcOfferExpanded}
                                onToggleMcOfferSection={setMcOfferExpanded}
                                onMcOfferChange={setMcOfferForm}
                                onImportMcOfferFromUrl={handleImportMcOfferFromUrl}
                                onDeleteMcOffer={handleDeleteMcOffer}
                                mcStateData={mcStateData}
                                onToggleMcState={handleToggleMcState}
                                selectedStockId={selectedStockId}
                                retailerOffers={retailerOffers}
                                onAddRetailerOffer={handleAddRetailerOffer}
                                onDeleteRetailerOffer={handleDeleteRetailerOffer}
                            />
                        </Paper>
                    </Stack>
                )}

                {tabIndex === 2 && <AdminProductUpdates authHeaders={authHeaders} />}

                {tabIndex === 1 && (
                    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Premium</TableCell>
                                        <TableCell>Comp until</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{user.name || 'Unnamed user'}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.premium ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>
                                                {user.complimentaryUntil
                                                    ? String(user.complimentaryUntil).slice(0, 10)
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>{user.role}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {roleOptions.map((option) => (
                                                        <Button
                                                            key={option}
                                                            variant={user.role === option ? 'contained' : 'outlined'}
                                                            size="small"
                                                            onClick={() => handleRoleChange(user.id, option)}
                                                            disabled={loading}
                                                        >
                                                            {option}
                                                        </Button>
                                                    ))}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => setComp({ user, date: '' })}
                                                        disabled={loading}
                                                    >
                                                        Comp
                                                    </Button>
                                                    {user.complimentaryUntil && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            onClick={() => setComplimentary(user.id, null)}
                                                            disabled={loading}
                                                        >
                                                            End comp
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={userTotal}
                            page={userPage}
                            onPageChange={(event, newPage) => setUserPage(newPage)}
                            rowsPerPage={userRowsPerPage}
                            onRowsPerPageChange={(event) => {
                                setUserRowsPerPage(parseInt(event.target.value, 10));
                                setUserPage(0);
                            }}
                            rowsPerPageOptions={[10, 20, 50]}
                        />
                    </Paper>
                )}
            </Paper>

            <Dialog open={Boolean(comp)} onClose={() => setComp(null)}>
                <DialogTitle>Complimentary membership</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {comp?.user?.email} gets premium through the chosen day, with no Stripe
                        subscription behind it. This is the right lever for comping someone — a
                        100%-off coupon or a paused subscription leaves Stripe reporting "active"
                        while nothing will ever renew it.
                    </DialogContentText>
                    <TextField
                        type="date"
                        size="small"
                        sx={{ mt: 2 }}
                        value={comp?.date || ''}
                        onChange={(event) => setComp({ ...comp, date: event.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setComp(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!comp?.date} onClick={handleGrantComp}>
                        Grant
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminPage;
